// Question + practice API shapes. Two layers on purpose:
//
//   - questionPayloadSchema mirrors the seed JSON / DB `payload` column.
//     Contains the canonical correct_index — server-only, never sent to clients.
//   - publicQuestionSchema is what the API hands to the frontend. correct_index
//     is stripped; options are tagged with stable ids (`opt-0`..`opt-3`) so the
//     frontend can shuffle without losing the mapping back to the server-side
//     answer key. The mapping check happens server-side on /answer.

import { z } from 'zod'
import { artworkItemIdSchema, kebabIdSchema, packIdSchema } from './api.ts'

// ---------- Internal (DB payload) ----------

const optionsArraySchema = z.array(z.string().min(1)).length(4)

/**
 * Shape of `questions.payload` in the DB and seed JSON. The two question types
 * share most fields and differ only by whether `image_url` is required.
 */
export const questionPayloadSchema = z
  .object({
    options_zh: optionsArraySchema,
    options_en: optionsArraySchema,
    correct_index: z.number().int().min(0).max(3),
    scripture_ref: z.string().min(1),
    image_url: z.string().min(1).optional(),
    /**
     * 常見誤區 — one sentence naming the common wrong line of thinking.
     * Authored per variant_group (identical across the group), server-only
     * like correct_index: surfaces via the answer response on wrong answers,
     * never via publicQuestionSchema. Optional so pre-review seed data and
     * the pre-re-seed DB window stay valid.
     */
    misconception_zh: z.string().min(1).optional(),
    misconception_en: z.string().min(1).optional(),
  })
  .strict()

export type QuestionPayload = z.infer<typeof questionPayloadSchema>

export const QUESTION_TYPES = ['mcq', 'image_id'] as const
export const questionTypeSchema = z.enum(QUESTION_TYPES)
export type QuestionType = z.infer<typeof questionTypeSchema>

// ---------- Public (server → client) ----------

/**
 * Stable id for an option position. Server emits in the seed's original order;
 * the frontend shuffles before render.
 */
export const optionIdSchema = z
  .string()
  .regex(/^opt-[0-3]$/, 'must be opt-0..opt-3')
export type OptionId = z.infer<typeof optionIdSchema>

export const OPTION_IDS: readonly OptionId[] = ['opt-0', 'opt-1', 'opt-2', 'opt-3']

export function optionIdAt(index: number): OptionId {
  if (index < 0 || index > 3 || !Number.isInteger(index)) {
    throw new Error(`option index out of range: ${index}`)
  }
  return OPTION_IDS[index]!
}

export const publicOptionSchema = z
  .object({
    id: optionIdSchema,
    textZh: z.string().min(1),
    textEn: z.string().min(1),
  })
  .strict()
export type PublicOption = z.infer<typeof publicOptionSchema>

export const publicQuestionSchema = z
  .object({
    id: z.string().min(1),
    packId: packIdSchema,
    topicId: kebabIdSchema,
    type: questionTypeSchema,
    promptZh: z.string().min(1),
    promptEn: z.string().min(1),
    options: z.array(publicOptionSchema).length(4),
    scriptureRef: z.string().min(1),
    /** Only present for `image_id` — path under /assets/... */
    imageUrl: z.string().min(1).optional(),
    variantGroup: z.string().min(1),
    difficulty: z.number().int().min(1).max(5),
  })
  .strict()
export type PublicQuestion = z.infer<typeof publicQuestionSchema>

/**
 * Project a stored question (raw payload + meta) into the public shape.
 * Strips correct_index. Throws if payload doesn't validate — callers should
 * surface the question id so seed bugs are easy to find.
 */
export function toPublicQuestion(input: {
  id: string
  packId: string
  topicId: string
  questionType: string
  promptZh: string
  promptEn: string
  payload: unknown
  variantGroup: string
  difficulty: number
}): PublicQuestion {
  const payload = questionPayloadSchema.parse(input.payload)
  const type = questionTypeSchema.parse(input.questionType)
  const packId = packIdSchema.parse(input.packId)
  const topicId = kebabIdSchema.parse(input.topicId)

  const options: PublicOption[] = payload.options_zh.map((textZh, i) => ({
    id: optionIdAt(i),
    textZh,
    textEn: payload.options_en[i]!,
  }))

  return {
    id: input.id,
    packId,
    topicId,
    type,
    promptZh: input.promptZh,
    promptEn: input.promptEn,
    options,
    scriptureRef: payload.scripture_ref,
    imageUrl: payload.image_url,
    variantGroup: input.variantGroup,
    difficulty: input.difficulty,
  }
}

/** Server-side helper to resolve the correct option id from a stored payload. */
export function correctOptionIdFromPayload(payload: QuestionPayload): OptionId {
  return optionIdAt(payload.correct_index)
}

/**
 * Server-side helper: misconception text to attach to an answer response.
 * Only wrong answers get the text (correct answers ride the celebration flow
 * with no reading time); questions without reviewed copy yield null.
 */
export function misconceptionForAnswer(
  payload: QuestionPayload,
  isCorrect: boolean,
): string | null {
  if (isCorrect) return null
  return payload.misconception_zh ?? null
}

// ---------- API: GET /api/practice/:packId/queue ----------

/**
 * Per-round target-topic banner — what the kid is "attacking" right now.
 * `null` everywhere when the user has unlocked every topic in the pack and
 * the round is review mode.
 */
export const queueTargetTopicSchema = z
  .object({
    topicId: kebabIdSchema,
    nameZh: z.string(),
    /** distinct-correct count the user already has on this topic. 0..total. */
    distinctCorrect: z.number().int().nonnegative(),
    /** total questions in this topic — typically 7 for v0.1. */
    total: z.number().int().positive(),
    /**
     * Question ids the user has already answered correctly at least once for
     * this topic. Lets the frontend tell whether a fresh correct answer
     * actually advances the unlock progress (new qid) or just reinforces an
     * existing distinct-correct (same qid the user already had).
     */
    correctQids: z.array(z.string()),
  })
  .strict()
export type QueueTargetTopic = z.infer<typeof queueTargetTopicSchema>

export const practiceQueueResponseSchema = z
  .object({
    packId: packIdSchema,
    questions: z.array(publicQuestionSchema),
    targetTopic: queueTargetTopicSchema.nullable(),
  })
  .strict()
export type PracticeQueueResponse = z.infer<typeof practiceQueueResponseSchema>

// ---------- API: POST /api/practice/answer ----------

/**
 * 'YYYY-MM-DD' from the device's local timezone. Step 10 uses this to drive
 * streak math — the server treats it as authoritative and never re-derives a
 * date from `Date.now()`. Trade-off accepted: device clock skew can be gamed,
 * which is fine for v0.1 (5–10 kids in one Sunday school).
 */
export const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')

export const practiceAnswerRequestSchema = z
  .object({
    questionId: z.string().min(1),
    chosenOptionId: optionIdSchema,
    /** Wall-clock ms from question render to submit. Bounded to keep outliers from skewing analytics. */
    timeTakenMs: z.number().int().min(0).max(10 * 60 * 1000),
    /** Device-local date the answer was submitted on. Drives streak math. */
    localDate: localDateSchema,
    /**
     * 0 = first try, 1 = first retry after a wrong answer, etc. Drives the
     * XP-by-attempt curve (first-try +10 / 1st retry +5 / 2nd +2 / ≥3rd +1).
     * Bounded so a runaway client can't fake an absurd index.
     */
    attemptIndex: z.number().int().min(0).max(20),
  })
  .strict()
export type PracticeAnswerRequest = z.infer<typeof practiceAnswerRequestSchema>

export const practiceAnswerResponseSchema = z
  .object({
    isCorrect: z.boolean(),
    correctOptionId: optionIdSchema,
    /**
     * Item id (kebab, no pack prefix) that just unlocked because this answer
     * was the last missing correct in its topic. `null` when no unlock fired —
     * either the topic was already complete, or there are still questions left.
     */
    unlockedItemId: kebabIdSchema.nullable(),
    /**
     * Chinese display name of the unlocked item — surfaced verbatim in the
     * unlock overlay headline. Always paired with unlockedItemId (both null
     * or both set).
     */
    unlockedItemNameZh: z.string().nullable(),
    /** XP delta this answer earned (0 wrong, 10 correct). Lets the client animate. */
    xpDelta: z.number().int().nonnegative(),
    /** Pack-level total XP after this answer, for the in-flight XP bar. */
    packTotalXp: z.number().int().nonnegative(),
    /** User-level current streak after this answer. Same value across packs. */
    currentStreak: z.number().int().nonnegative(),
    /**
     * 常見誤區 text for the answered question. Non-null only when isCorrect
     * is false AND the question has reviewed misconception content — the
     * wrong-feedback card reads at the kid's own pace. Null on correct
     * answers (celebration flow has no reading time) and on questions whose
     * misconception copy isn't seeded yet.
     */
    misconceptionZh: z.string().nullable(),
  })
// Intentionally NOT .strict() — clients parse this response, and a future
// server-only field addition must not break already-deployed bundles (the
// round-1 sceneItemSchema incident: SW-cached clients hit unrecognized_keys
// and PackHomePage broke for every returning user). Strict checks belong on
// request schemas (server-side) where extra fields ARE bugs.
export type PracticeAnswerResponse = z.infer<typeof practiceAnswerResponseSchema>

// ---------- API: GET /api/packs/:packId/scene ----------

export const sceneItemSchema = z
  .object({
    itemId: kebabIdSchema,
    nameZh: z.string().min(1),
    nameEn: z.string().min(1),
    scriptureRef: z.string().min(1),
    /** Bilingual description from topics table. May be empty if topic row lacks it. */
    descriptionZh: z.string(),
    descriptionEn: z.string(),
    /** True once topic completion has filed it into user_items. */
    isUnlocked: z.boolean(),
    /** True once a user_artworks row for this item is marked complete. */
    isComplete: z.boolean(),
    /**
     * Presigned R2 GET URL for the user's colored artwork. Always null when
     * isComplete is false; null even when complete if signing fails.
     */
    artworkUrl: z.string().url().nullable(),
    /** Artwork id for the latest completed artwork; null when none. */
    artworkId: z.string().nullable(),
    /**
     * Unix ms timestamp from user_items.detail_seen_at. Null when the user
     * has never opened this item's detail page (or item is not yet unlocked,
     * since user_items row only exists after unlock). Used by ItemDetailPage
     * to default first visit → 影片故事 tab; subsequent visits → 彩繪填圖 tab.
     */
    detailSeenAt: z.number().int().positive().nullable(),
  })
// Intentionally NOT .strict() — this schema is used client-side to parse
// server responses, and a future server-only field addition must not break
// already-deployed clients still running an older bundle (which the new
// field would otherwise fail with "unrecognized_keys"). Round-1 alpha
// shipped detailSeenAt as a SceneItem field after Service Workers had
// already cached an older bundle, and the strict check broke PackHomePage
// for every returning user until they refreshed twice. Strict checks
// belong on request schemas (server-side) where extra fields ARE bugs.
export type SceneItem = z.infer<typeof sceneItemSchema>

export const packSceneResponseSchema = z
  .object({
    packId: packIdSchema,
    items: z.array(sceneItemSchema),
  })
  .strict()
export type PackSceneResponse = z.infer<typeof packSceneResponseSchema>

// ---------- API: artworks (Step 9) ----------
//
// Open → autosave → complete → get. Two presigned URLs (artwork PNG +
// thumbnail JPG) ride along with the open / autosave responses so the
// frontend can PUT R2 directly without round-tripping through the Worker
// for the bytes themselves. URLs expire ~30min; PUT autosave can re-sign.

export const artworkOpenRequestSchema = z
  .object({
    packId: packIdSchema,
    // artworkItemIdSchema (not kebabIdSchema) — accepts bonus snake_case
    // id (joseph_panorama) which is the codebase's intentional exception.
    itemId: artworkItemIdSchema,
  })
  .strict()
export type ArtworkOpenRequest = z.infer<typeof artworkOpenRequestSchema>

export const artworkOpenResponseSchema = z
  .object({
    artworkId: z.string().min(1),
    /** True when an in-progress (isComplete=false) row was reused. */
    isResume: z.boolean(),
    fillActionCount: z.number().int().nonnegative(),
    putUrl: z.string().url(),
    thumbnailPutUrl: z.string().url(),
    urlsExpireAt: z.number().int().nonnegative(),
    /** Presigned GET for the in-progress PNG; null on a brand-new artwork. */
    resumeGetUrl: z.string().url().nullable(),
  })
  .strict()
export type ArtworkOpenResponse = z.infer<typeof artworkOpenResponseSchema>

export const artworkAutosaveRequestSchema = z
  .object({
    /** Optional bump — server stores max(prev, this) so retries are idempotent. */
    fillActionCount: z.number().int().nonnegative().optional(),
  })
  .strict()
export type ArtworkAutosaveRequest = z.infer<typeof artworkAutosaveRequestSchema>

export const artworkAutosaveResponseSchema = z
  .object({
    putUrl: z.string().url(),
    thumbnailPutUrl: z.string().url(),
    urlsExpireAt: z.number().int().nonnegative(),
  })
  .strict()
export type ArtworkAutosaveResponse = z.infer<typeof artworkAutosaveResponseSchema>

export const artworkCompleteRequestSchema = z
  .object({
    fillActionCount: z.number().int().nonnegative(),
  })
  .strict()
export type ArtworkCompleteRequest = z.infer<typeof artworkCompleteRequestSchema>

export const artworkCompleteResponseSchema = z
  .object({
    artworkId: z.string().min(1),
    completedAt: z.number().int().nonnegative(),
    /**
     * True iff this complete flipped user_progress.bonus_unlocked false→true
     * for this (user, pack). Drives capstone-reveal phase on the frontend.
     * False when: not the last item, already unlocked, or the completed
     * artwork IS the bonus artwork itself. Step 13 (final-reward).
     */
    bonusJustUnlocked: z.boolean(),
  })
  .strict()
export type ArtworkCompleteResponse = z.infer<typeof artworkCompleteResponseSchema>

export const artworkGetResponseSchema = z
  .object({
    id: z.string().min(1),
    packId: packIdSchema,
    // artworkItemIdSchema — same reason as artworkOpenRequestSchema.itemId.
    itemId: artworkItemIdSchema,
    fillActionCount: z.number().int().nonnegative(),
    isComplete: z.boolean(),
    completedAt: z.number().int().nonnegative().nullable(),
    getUrl: z.string().url(),
    thumbnailGetUrl: z.string().url().nullable(),
  })
  .strict()
export type ArtworkGetResponse = z.infer<typeof artworkGetResponseSchema>

// ---------- API: pack bonus state (Step 13) ----------
//
// GET /api/packs/:packId/bonus — drives PackScene CapstoneSection + the
// bonus-route gating in ColoringPage. Three states encoded by the same
// payload via the (isUnlocked, isComplete) tuple:
//   • locked            isUnlocked=false                          → silhouette
//   • unlocked-pending  isUnlocked=true,  isComplete=false        → "Color the panorama" CTA
//   • completed         isUnlocked=true,  isComplete=true         → user artwork + share
//
// lineartUrl is a static asset path (Pages serves it). artworkUrl is a
// presigned R2 GET, only present when the bonus artwork is complete.
// bonusItemId carries PACKS[packId].bonus.id so the frontend can navigate
// to /pack/:packId/bonus and the share card can label it.

export const packBonusResponseSchema = z
  .object({
    packId: packIdSchema,
    bonusItemId: z.string().min(1),
    nameZh: z.string().min(1),
    nameEn: z.string().min(1),
    scriptureRef: z.string().min(1),
    isUnlocked: z.boolean(),
    isComplete: z.boolean(),
    /** Static asset path, e.g. /assets/packs/joseph/bonus/panorama-lineart.png. */
    lineartUrl: z.string().min(1),
    /** Presigned R2 GET for the user's colored bonus artwork; null when not complete. */
    artworkUrl: z.string().url().nullable(),
    /** Artwork id for the bonus artwork row; null when never opened. */
    artworkId: z.string().nullable(),
  })
  .strict()
export type PackBonusResponse = z.infer<typeof packBonusResponseSchema>
